import React, { useEffect, useState, useSyncExternalStore } from "react";
import { toast } from "react-toastify";
import { FileSearch2, Loader2, RotateCcw, Search, Trash2 } from "lucide-react";

import { deletedDocumentsActionStore } from "../documentActionStores";

import {
  getAllDeletedDocuments,
  restoreDeletedDocumentSql,
} from "../../Apis_chatbot/apis";
import {
  checkSystemHealthCheck,
  getReadableApiErrorMessage,
  restoreDocument,
  permanentlyDeleteDocument,
} from "../../Api_Qdrant/Fast_apis";
import {
  getFastApiErrorMessage,
  isFastApiError,
} from "../../Api_Qdrant/fastApiError";
import ConfirmDialog from "../../app/components/ConfirmDialog/ConfirmDialog";

import "./DeletedDocuments.css";

const formatDate = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const normalizeText = (text) => (typeof text === "string" ? text.trim() : "");
const formatStatusLabel = (status) =>
  String(status || "unknown")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());

const QDRANT_NOT_READY_MESSAGE =
  "Qdrant DB is not running or there is some problem. Restore cannot be done until Qdrant DB is available.";
const DELETE_PENDING_STATUS = "delete_pending";
const DELETED_STATUS = "deleted";

function DeletedDocuments() {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState("");
  const [error, setError] = useState("");

  // Document pending a restore confirmation in the popup.
  const [restoreTarget, setRestoreTarget] = useState(null);

  // Document pending a permanent-delete confirmation in the popup.
  const [permanentDeleteTarget, setPermanentDeleteTarget] = useState(null);

  // Restore / permanent-delete progress lives in a module-level store so the
  // button loading state survives the user switching tabs mid-request.
  const { restoringDocId, permanentlyDeletingDocId } = useSyncExternalStore(
    deletedDocumentsActionStore.subscribe,
    deletedDocumentsActionStore.getSnapshot
  );

  const setRestoringDocId = (id) =>
    deletedDocumentsActionStore.set("restoringDocId", id);

  const setPermanentlyDeletingDocId = (id) =>
    deletedDocumentsActionStore.set("permanentlyDeletingDocId", id);

  // True while a restore or permanent-delete is in flight for the given row.
  // Every action button for that row must stay disabled until it settles.
  const isDocBusy = (docId) =>
    Boolean(docId) &&
    (restoringDocId === docId ||
      permanentlyDeletingDocId === docId);

  const loadDeletedDocuments = async () => {
    setLoading(true);
    setError("");

    try {
      const res = await getAllDeletedDocuments();
      const list = Array.isArray(res?.result_value) ? res.result_value : [];
      setDocuments(list);
    } catch (err) {
      console.error("Failed to load deleted documents:", err);
      setError(
        "We could not load deleted documents right now. Please try again."
      );
      toast.error("Failed to load deleted documents");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Drop any restore/delete flag left over from a request that never
    // settled before a previous unmount.
    deletedDocumentsActionStore.pruneStale();

    loadDeletedDocuments();
  }, []);

  const restoreViaSql = async (docId) => {
    const res = await restoreDeletedDocumentSql(docId);
    const hasError =
      res?.error || res?.error_value || res?.status_code >= 400;

    if (hasError || res?.result_value === false) {
      toast.error(
        getReadableApiErrorMessage(
          res?.error_value || res?.error || res,
          "Restore failed"
        )
      );
      return false;
    }

    toast.success("Document restored successfully");
    return true;
  };

  // Returns true when the server state may have changed and the list should be
  // refetched (full success OR a partial restore), false when nothing changed.
  const restoreViaQdrant = async (docId) => {
    const res = await restoreDocument(docId);

    // Rate-limited: the interceptor already showed a friendly toast — just stop.
    if (res?.rateLimited) return false;

    if (isFastApiError(res)) {
      toast.error(getFastApiErrorMessage(res, "Restore failed"));
      return false;
    }

    if (res?.vector_store_synced === false) {
      toast.error(
        "Restored in SQL but Qdrant sync failed. Document may not be searchable yet."
      );
      // SQL row was still restored — the list must refresh.
      return true;
    }

    toast.success("Document restored successfully");
    return true;
  };

  // Opens the restore confirmation popup.
  const handleRestore = (doc) => {
    const docId = doc?.qdrantDocId;
    if (!docId) {
      toast.error("Document qdrantDocId is missing");
      return;
    }

    // Another action is already running for this document.
    if (isDocBusy(docId)) return;

    setRestoreTarget(doc);
  };

  // Runs after the user confirms in the popup.
  const confirmRestore = async () => {
    const doc = restoreTarget;
    const docId = doc?.qdrantDocId;

    if (!docId) {
      setRestoreTarget(null);
      return;
    }

    const statusKey = (doc?.status || "").toLowerCase();
    const isDeletePending = statusKey === DELETE_PENDING_STATUS;

    setRestoringDocId(docId);

    try {
      let restored = false;

      if (isDeletePending) {
        // Vectors were never actually removed from Qdrant, so no health
        // check is needed here — just flip the SQL status back.
        restored = await restoreViaSql(docId);
      } else {
        const healthRes = await checkSystemHealthCheck();

        // Rate-limited: the interceptor already showed a friendly toast — just stop.
        if (healthRes?.rateLimited) return;

        const qdrantStatus = healthRes?.data?.checks?.qdrant?.status;

        if (qdrantStatus !== "ok") {
          toast.error(QDRANT_NOT_READY_MESSAGE);
          return;
        }

        restored = await restoreViaQdrant(docId);
      }

      if (restored) {
        await loadDeletedDocuments();
      }
    } catch (err) {
      console.error("Restore failed:", err);
      toast.error(getFastApiErrorMessage(err, "Restore failed"));

      // State may have changed before the error — resync the list.
      await loadDeletedDocuments();
    } finally {
      setRestoringDocId("");
      setRestoreTarget(null);
    }
  };

  // Opens the permanent-delete confirmation popup.
  const handlePermanentDelete = (doc) => {
    const docId = doc?.qdrantDocId;
    if (!docId) {
      toast.error("Document qdrantDocId is missing");
      return;
    }

    // Another action is already running for this document.
    if (isDocBusy(docId)) return;

    setPermanentDeleteTarget(doc);
  };

  // Runs after the user confirms in the popup.
  const confirmPermanentDelete = async () => {
    const doc = permanentDeleteTarget;
    const docId = doc?.qdrantDocId;

    if (!docId) {
      setPermanentDeleteTarget(null);
      return;
    }

    setPermanentlyDeletingDocId(docId);

    try {
      const res = await permanentlyDeleteDocument(docId);

      // Rate-limited: the interceptor already showed a friendly toast — just stop.
      if (res?.rateLimited) return;

      if (isFastApiError(res)) {
        toast.error(
          getFastApiErrorMessage(res, "Permanent delete failed")
        );
        return;
      }

      toast.success("Document permanently deleted");
      await loadDeletedDocuments();
    } catch (err) {
      console.error("Permanent delete failed:", err);
      toast.error(getFastApiErrorMessage(err, "Permanent delete failed"));
    } finally {
      setPermanentlyDeletingDocId("");
      setPermanentDeleteTarget(null);
    }
  };

  const filteredDocuments = documents.filter((doc) => {
    const query = searchText.toLowerCase();
    return (
      normalizeText(doc.docName).toLowerCase().includes(query) ||
      normalizeText(doc.qdrantDocId).toLowerCase().includes(query)
    );
  });

  return (
    <div className="deleted-documents-page">
      <div className="deleted-documents-hero">
        <div>
          <p className="deleted-documents-eyebrow">ChatBot Library</p>
          <h1>Deleted Documents</h1>
          {/* <p className="deleted-documents-subtitle">
            Documents removed from SQL. Restoring is only allowed while
            Qdrant is healthy.
          </p> */}
           <p className="deleted-documents-subtitle">
            Documents that have been removed can be restored.
          </p>
        </div>

        <div className="deleted-documents-summary">
          <div className="summary-card">
            <span>Total Deleted</span>
            <strong>{documents.length}</strong>
          </div>
        </div>
      </div>

      <div className="deleted-documents-panel">
        <div className="panel-header">
          {/* <div>
            <h2>Deleted Documents</h2>
            <p>Documents that have been removed and can be restored.</p>
          </div> */}
          <div className="search-box">
            <Search size={16} />
            <input
              type="text"
              placeholder="Search document"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />
          </div>
        </div>

        {loading ? (
          <div className="state-box">
            <Loader2 className="spin" size={22} />
            <p>Loading deleted documents...</p>
          </div>
        ) : filteredDocuments.length > 0 ? (
          <div className="deleted-documents-table-wrap">
            <table className="deleted-documents-table">
              <thead>
                <tr>
                  <th>Document</th>
                  <th>Qdrant Doc ID</th>
                  <th>Chunks</th>
                  <th>Status</th>
                  <th>Deleted At</th>
                  <th>
                    Action
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredDocuments.map((doc) => {
                  const docKey = doc.qdrantDocId || doc.id;
                  const statusKey = (doc.status || "").toLowerCase();

                  // Any action running for this row locks its other actions.
                  const docBusy = isDocBusy(docKey);

                  return (
                    <tr key={docKey}>
                      <td>
                        <div className="doc-name-cell">
                          <Trash2 size={16} />
                          <span title={doc.docName}>{doc.docName}</span>
                        </div>
                      </td>
                      <td className="mono-cell" title={doc.qdrantDocId}>
                        {doc.qdrantDocId}
                      </td>
                      <td>{doc.chunksCount || 0}</td>
                      <td>
                        <span
                          className={`status-badge ${statusKey || "unknown"}`}
                        >
                          {formatStatusLabel(doc.status)}
                        </span>
                      </td>
                      <td>
                        {
                        doc.deletedAt.startsWith("0001-01-01") || doc.status === "delete_pending" ? (
                          "--"
                        ) : (
                          formatDate(doc.deletedAt)
                        )}
                      </td>
                      <td>
                        <div className="deleted-doc-actions">
                          <button
                            type="button"
                            className="restore-btn"
                            onClick={() => handleRestore(doc)}
                            disabled={docBusy}
                          >
                            {restoringDocId === docKey ? (
                              <>
                                <Loader2 className="spin" size={12} />
                                Checking Qdrant
                              </>
                            ) : (
                              <>
                                <RotateCcw size={12} />
                                Restore
                              </>
                            )}
                          </button>
                          {statusKey === DELETED_STATUS && (
                            <button
                              type="button"
                              className="permanent-delete-btn"
                              onClick={() => handlePermanentDelete(doc)}
                              disabled={docBusy}
                            >
                              {permanentlyDeletingDocId === docKey ? (
                                <>
                                  <Loader2 className="spin" size={12} />
                                  Deleting
                                </>
                              ) : (
                                <>
                                  <Trash2 size={12} />
                                  Delete Permanently
                                </>
                              )}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="state-box">
            <FileSearch2 size={22} />
            <p>No deleted documents found.</p>
          </div>
        )}
      </div>

      {error && <div className="deleted-documents-error">{error}</div>}

      <ConfirmDialog
        open={Boolean(restoreTarget)}
        tone="primary"
        title="Restore document?"
        message={
          <>
            <strong>{restoreTarget?.docName}</strong> will be moved back to your active documents. Do you want to proceed?
          </>
        }
        confirmLabel="Restore"
        cancelLabel="Cancel"
        loading={
          Boolean(restoreTarget) &&
          restoringDocId === restoreTarget?.qdrantDocId
        }
        onConfirm={confirmRestore}
        onCancel={() => setRestoreTarget(null)}
      />

      <ConfirmDialog
        open={Boolean(permanentDeleteTarget)}
        tone="danger"
        title="Permanently delete document?"
        message={
          <>
            <strong>{permanentDeleteTarget?.docName}</strong> will be permanently deleted and cannot be restored. Are you sure you want to proceed?
          </>
        }
        confirmLabel="Delete permanently"
        cancelLabel="Cancel"
        loading={
          Boolean(permanentDeleteTarget) &&
          permanentlyDeletingDocId === permanentDeleteTarget?.qdrantDocId
        }
        onConfirm={confirmPermanentDelete}
        onCancel={() => setPermanentDeleteTarget(null)}
      />
    </div>
  );
}

export default DeletedDocuments;
